import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faPrint, faCircleCheck, faShieldHalved } from '@fortawesome/free-solid-svg-icons'
import { faFileExcel, faFilePdf, faTrashCan, faFile, faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons'


import {
    APIConstants,
    ClaimApprovalConstants,
    CommonAPIConstants,
    formatDOSDate,
} from "../utilities/Constants";
import {
    Search,
    RefreshCw,
    Filter as FilterIcon,
    X,
    Download,
    Trash2,
    ChevronUp,
    ChevronDown,
    FolderPlus,
    Files,
    FolderOpen,
    RotateCcw,
    CheckCircle2,
    Send,
    Upload,
    BookUser,
    Delete,
    Trash,
    Folder,
    Pencil,
    Plus,
    ChevronRight,
    ChevronLeft,
} from "lucide-react";

/* ============================================================================
   CONFIG — replace these with your real values (env vars, auth context, etc.)
   ========================================================================== */


const COLUMNS = [
    { key: "dos", label: "DOS", filterLabel: "DOS", width: "w-24" },
    { key: "docType", label: "Document Type", filterLabel: "Document Type", width: "flex-1" },
    { key: "claimId", label: "Claim ID", filterLabel: "Claim ID", width: "w-24" },
    { key: "cpt", label: "CPT Code", filterLabel: "CPT", width: "w-20" },
    { key: "payerRef", label: "Payer Ref Number", filterLabel: "Payer Ref #", width: "w-28" },
    { key: "signedOff", label: "Signed Off", filterLabel: "Name / date", width: "w-32" },
    { key: "supervisorSignedOff", label: "Supervisor Signed Off", filterLabel: "Name / date", width: "w-32" },
    { key: "pages", label: "# Pages", filterLabel: "Pages", width: "w-16" },
];

/* ============================================================================
   Small reusable bits
   ========================================================================== */
function SortableHeader({ label }) {
    return (
        <button type="button" className="hv-table__sort-toggle">
            {label}
            <span className="hv-table__sort-toggle__icons">
                <ChevronUp size={9} />
                <ChevronDown size={9} />
            </span>
        </button>
    );
}

function Section({ title, defaultOpen = true, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="hv-accordion">
            <button type="button" onClick={() => setOpen((o) => !o)} className="hv-accordion__head">
                <span className="hv-accordion__title">{open ? "▾" : "▸"} {title}</span>
            </button>
            {open && <div className="hv-accordion__body">{children}</div>}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="hv-field">
            {label && <label className="hv-field__label">{label}</label>}
            {children}
        </div>
    );
}

const inputCls = "hv-input";

/* ============================================================================
   Main component
   ========================================================================== */
export default function FaxDocuments() {
    const [activeTab, setActiveTab] = useState("new");

    // ---- Patient search state ----
    const [searchText, setSearchText] = useState("");
    const [selectedSearchFilter, setSelectedSearchFilter] = useState("Name");
    const [patientResults, setPatientResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [patientLoading, setPatientLoading] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState('');
    const [usingMockPatients, setUsingMockPatients] = useState(false);
    const debounceRef = useRef(null);

    // ---- Date filter state ----
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // ---- Documents table state ----
    const [documents, setDocuments] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [usingMockDocs, setUsingMockDocs] = useState(false);
    const [selectedRows, setSelectedRows] = useState('');

    // ---- Right panel state ----
    const [coverageName, setCoverageName] = useState("");
    const [coverageMessage, setCoverageMessage] = useState("");
    const [senderFrom, setSenderFrom] = useState("Test Practice");
    const [senderPhone, setSenderPhone] = useState("(987) 654-3210");
    const [senderFax, setSenderFax] = useState("(987) 654-3210");
    const [recipientTo, setRecipientTo] = useState("");
    const [destinationFax, setDestinationFax] = useState("");
    const [sendStatus, setSendStatus] = useState("");

    const [suggestions, setSuggestions] = useState([]);

    const userData = useSelector((state) => state.user.user);
    const sAuthToken = useSelector((state) => state.user.token);
    const userPracticeID = userData ? userData.practiceID : null;
    const userId = userData ? userData.userID : null;

    const [startDate, setStartDate] = useState(''); // State to store start date
    const [endDate, setEndDate] = useState('');
    const [isStartOpen, setIsStartOpen] = useState(false);
    const [isEndOpen, setIsEndOpen] = useState(false);
    const [showAddressBook, setShowAddressBook] = useState(false);
    const [addressBook, setAddressBook] = useState([]);
    const [searchContact, setSearchContact] = useState("");
    const [showFolderPopup, setShowFolderPopup] = useState(false);
    const [selectedPatientName, setSelectedPatientName] = useState("");

    const [folderName, setFolderName] = useState("");
    const [fromDOS, setFromDOS] = useState("");
    const [toDOS, setToDOS] = useState("");
    const [folders, setFolders] = useState([]);
    const [expandedFolders, setExpandedFolders] = useState({});
    const [showSubFolderPopup, setShowSubFolderPopup] = useState(false);
    const [parentFolderId, setParentFolderId] = useState(0);
    const [folderSearch, setFolderSearch] = useState({});
    const [expandedFolder, setExpandedFolder] = useState(null);
    const [parentFolderName, setParentFolderName] = useState("");
    const [showRenameFolderPopup, setShowRenameFolderPopup] = useState(false);
    const [currentPage, setCurrentPage] = useState({});
    const [successMessage, setSuccessMessage] = useState("");
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [showSelectionPopup, setShowSelectionPopup] = useState(false);
    const [selectedDocumentId, setSelectedDocumentId] = useState("");
    const [showMovePopup, setShowMovePopup] = useState(false);
    const [moveFolderId, setMoveFolderId] = useState(null);
    const [moveFolderName, setMoveFolderName] = useState("");
    const [showMoveSuccess, setShowMoveSuccess] = useState(false);
    const [showDeleteFilePopup, setShowDeleteFilePopup] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showDeleteFileSuccess, setShowDeleteFileSuccess] = useState(false);
    const [showPdfPopup, setShowPdfPopup] = useState(false);
    const [pdfUrl, setPdfUrl] = useState("");
    const [sentFaxes, setSentFaxes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startSentDate, setStartSentDate] = useState("");
    const [endSentDate, setendSentDate] = useState("");
    const [filteredDocs, setFilteredDocs] = useState([]);
    const [isDateFilterApplied, setIsDateFilterApplied] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [docsData, setDocsData] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [showAddContact, setShowAddContact] = useState(false);
    const [contactName, setContactName] = useState("");
    const [contactFax, setContactFax] = useState("");
    const [selectedFiles, setSelectedFiles] = useState({});
    const [searchType, setSearchType] = useState("claimID");
    const [searchKey, setSearchKey] = useState("");
    const [searchResult, setSearchResult] = useState([]);
    const [showMergeConfirm, setShowMergeConfirm] = useState(false);
    const [showMergeProgress, setShowMergeProgress] = useState(false);
    const [showMergeSuccess, setShowMergeSuccess] = useState(false);
    const [showMergeValidation, setShowMergeValidation] = useState(false);

    const [selectedMergeFiles, setSelectedMergeFiles] = useState([]);
    const [mergedFiles, setMergedFiles] = useState([]);
    const [mergedFolderOpen, setMergedFolderOpen] = useState(true);
    const [isSelectedchecked, setIsSelectedchecked] = useState(null);

    const itemsPerPage = 25;
    const fileInputRef = useRef(null);

    const [filters, setFilters] = useState({
        dos: "",
        docType: "",
        claimId: "",
        cpt: "",
        payerRef: "",
        signedOff: "",
        supervisorSignedOff: "",
        pages: "",
    });

    const [sortConfig, setSortConfig] = useState({
        key: "",
        direction: "asc",
    });


    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSort = (key) => {
        let direction = "asc";

        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }

        setSortConfig({
            key,
            direction,
        });
    };


    const filteredDocuments = [...documents]
        .filter((doc) => {
            return (
                (doc.DocDate || "")
                    .toLowerCase()
                    .includes(filters.dos.toLowerCase()) &&
                (doc.DocTypeName || "")
                    .toLowerCase()
                    .includes(filters.docType.toLowerCase()) &&
                (doc.ClaimID || "")
                    .toString()
                    .toLowerCase()
                    .includes(filters.claimId.toLowerCase()) &&
                (doc.CPTCode || "")
                    .toLowerCase()
                    .includes(filters.cpt.toLowerCase()) &&
                (doc.PayerRefNumber || "")
                    .toLowerCase()
                    .includes(filters.payerRef.toLowerCase()) &&
                (`${doc.FirstName || ""} ${doc.LastName || ""}`)
                    .toLowerCase()
                    .includes(filters.signedOff.toLowerCase()) &&
                (formatDOSDate(doc.SupervisorsignOffDate) || "")
                    .toLowerCase()
                    .includes(filters.supervisorSignedOff.toLowerCase()) &&
                (doc.DocNumberOfPages || "")
                    .toString()
                    .includes(filters.pages)
            );
        })
        .sort((a, b) => {
            if (!sortConfig.key) return 0;

            let aValue = "";
            let bValue = "";

            switch (sortConfig.key) {
                case "dos":
                    aValue = a.DocDate || "";
                    bValue = b.DocDate || "";
                    break;

                case "docType":
                    aValue = a.DocTypeName || "";
                    bValue = b.DocTypeName || "";
                    break;

                case "claimId":
                    aValue = a.ClaimID || "";
                    bValue = b.ClaimID || "";
                    break;

                case "cpt":
                    aValue = a.CPTCode || "";
                    bValue = b.CPTCode || "";
                    break;

                case "payerRef":
                    aValue = a.PayerRefNumber || "";
                    bValue = b.PayerRefNumber || "";
                    break;

                case "signedOff":
                    aValue = `${a.FirstName} ${a.LastName}`;
                    bValue = `${b.FirstName} ${b.LastName}`;
                    break;

                case "supervisorSignedOff":
                    aValue = formatDOSDate(a.SupervisorsignOffDate);
                    bValue = formatDOSDate(b.SupervisorsignOffDate);
                    break;

                case "pages":
                    aValue = Number(a.DocNumberOfPages);
                    bValue = Number(b.DocNumberOfPages);
                    break;

                default:
                    return 0;
            }

            if (typeof aValue === "number") {
                return sortConfig.direction === "asc"
                    ? aValue - bValue
                    : bValue - aValue;
            }

            return sortConfig.direction === "asc"
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        });

    const handleStartDateChange = (date) => {
        if (date) {
            const formattedDate = format(date, 'MM/dd/yyyy');
            setStartDate(formattedDate);

            setIsStartOpen(false);

        } else {
            setStartDate(null);
        }
    };


    const handleEndDateChange = (date) => {

        if (date) {
            const formattedDate = format(date, 'MM/dd/yyyy');
            setEndDate(formattedDate);

            setIsEndOpen(false);

        } else {
            setEndDate(null);
        }
    };



    const getMonths = (selectedYear) => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();

        return Array.from({ length: 12 }, (v, k) => {
            if (selectedYear === currentYear && k > currentMonth) {
                return null;
            }
            return (
                <option key={k} value={k}>
                    {format(new Date(selectedYear, k), 'MMMM')}
                </option>
            );
        }).filter(option => option !== null);
    };



    const handleTodayClick = (dateType) => {
        const today = new Date();

        if (dateType === "start" && startDate) {
            setStartDate(today);
            setIsStartOpen(false);
        }

        if (dateType === "end" && endDate) {
            setEndDate(today);
            setIsEndOpen(false);
        }
    };





    // Styled via .hv-cal-header / .hv-cal-nav-btn / .hv-cal-select, shared in
    // styles/healthiva/_forms.scss — one header renderer instead of a
    // duplicated inline closure per DatePicker (was 4 copies across this page).
    const renderCalendarHeader = ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => {
        const selectedDate = date instanceof Date && !isNaN(date) ? date : new Date();
        const selectedMonth = selectedDate.getMonth();
        const selectedYear = selectedDate.getFullYear();

        return (
            <div className="hv-cal-header">
                <button type="button" className="hv-cal-nav-btn" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                    {"<"}
                </button>
                <select
                    className="hv-cal-select"
                    value={selectedMonth}
                    onChange={({ target: { value } }) => changeMonth(Number(value))}
                >
                    {getMonths(selectedYear)}
                </select>
                <select
                    className="hv-cal-select"
                    value={selectedYear}
                    onChange={({ target: { value } }) => changeYear(Number(value))}
                >
                    {Array.from({ length: 100 }, (v, k) => (
                        <option key={k} value={new Date().getFullYear() - k}>
                            {new Date().getFullYear() - k}
                        </option>
                    ))}
                </select>
                <button type="button" className="hv-cal-nav-btn" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                    {">"}
                </button>
            </div>
        );
    };

    // One container factory for all 4 DatePickers on this page. "start"/"end"
    // are the New-tab date range; "startSent"/"endSent" are the Sent-tab date
    // range, which route to the (pre-existing) handleTodaySentClick handler.
    const makeCalContainer = (which) => ({ children }) => (
        <div className="hv-cal-container">
            {children}
            <div className="hv-cal-footer">
                <button
                    type="button"
                    className="today-btn"
                    onClick={() =>
                        which === "startSent" || which === "endSent"
                            ? handleTodaySentClick(which === "startSent" ? "start" : "end")
                            : handleTodayClick(which)
                    }
                >
                    Today
                </button>
            </div>
        </div>
    );


    // SENT Tabs Date Calendar

    const handleStartSentDateChange = (date) => {
        if (date) {
            const formattedDate = format(date, 'MM/dd/yyyy');
            setStartSentDate(formattedDate);

            setIsStartOpen(false);

        } else {
            setStartSentDate(null);
        }
    };


    const handleEndSentDateChange = (date) => {

        if (date) {
            const formattedDate = format(date, 'MM/dd/yyyy');
            setendSentDate(formattedDate);

            setIsEndOpen(false);

        } else {
            setendSentDate(null);
        }
    };




    const handleTodaySentClick = (dateType) => {
        const today = new Date();

        if (dateType === "start" && startSentDate) {
            setStartDate(today);
            setIsStartOpen(false);
        }

        if (dateType === "end" && endSentDate) {
            setEndDate(today);
            setIsEndOpen(false);
        }
    };
    /* ---------------- API: search patients ---------------- */


    const fetchSuggestion = () => {
        const inputJSON = {
            PracticeID: userPracticeID,
            SearchKey: searchText,
            SearchBy: 0,
            isFromLogin: 0
        };

        const sJSONInput = JSON.stringify(inputJSON);

        const url = `${APIConstants.BASE_URL_SEARCH}${CommonAPIConstants.GetAllPatientNames}?sJSONInput=${encodeURIComponent(sJSONInput)}`;

        fetch(url, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            }
        })
            .then((response) => {
                if (response.status !== 200) {
                    console.log("response.status", response.status);
                } else return response.json();
            })
            .then(data => {
                const parsedData = JSON.parse(data);
                const suggData = parsedData.Data;
                setSuggestions(suggData);


            })
    }





    const handleInputKeyPress = (event) => {
        if (event.key === 'Backspace') {

            if (searchText === '' && suggestions.length > 0) {
                setSuggestions([]);


            } else {

                const updatedSearchText = searchText.slice(0, -1);
                setSearchText(updatedSearchText);


                fetchSuggestions(updatedSearchText);
            }
        } else if (event.key !== 'Enter') {

            setSearchText(event.target.value);
            fetchSuggestions(event.target.value);
        }
    };



    const handleSuggestionSelect = (item) => {
        setSearchText(`${item.LastName}, ${item.FirstName}`);
        setSelectedPatientName(`${item.LastName}, ${item.FirstName}`);
        setSelectedPatient(item.PatientID);
        setSuggestions([]);
    };


    const fetchSuggestions = (text) => {
        if (!text) {
            setSuggestions([]);
            return;
        }

        fetchSuggestion();
    };



    useEffect(() => {
        document.title = "Send Fax";
        const delay = setTimeout(() => {
            if (searchText) {
                fetchSuggestion();
            }
        }, 400);

        return () => clearTimeout(delay);
    }, [searchText]);


    /* ---------------- API: get all documents for a patient ---------------- */
    const fetchDocuments = useCallback(async (patientID) => {
        if (!patientID) return;
        setDocsLoading(true);
        setUsingMockDocs(false);
        try {
            const objInput = {
                PracticeID: userPracticeID,
                PatientID: patientID,
            };
            const sInputJSON = JSON.stringify(objInput);
            const APIURL = `${APIConstants.PATIENT_PORTAL_URL}Document/GetPatientAllDocumentDetails?sInputJSON=${sInputJSON}`;

            const response = await fetch(APIURL, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sAuthToken}`,
                },
            });
            if (!response.ok) throw new Error(`Request failed with ${response.status}`);
            const data = await response.json();
            const parsedData = JSON.parse(data);
            console.log("GetPatientAllDocumentDetails response:", parsedData);

            setDocuments(parsedData);
            setFilteredDocs(parsedData)
        } catch (err) {
            console.warn("GetPatientAllDocumentDetails failed, using mock data:", err.message);
            setDocuments("");
            setUsingMockDocs(true);
        } finally {
            setDocsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedPatient) fetchDocuments(selectedPatient);
    }, [selectedPatient]);

    /* ---------------- Handlers ---------------- */


    const toggleRow = (id) => {
        setSelectedRows((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleAllRows = () => {
        if (selectedRows.size === documents.length) setSelectedRows(new Set());
        else setSelectedRows(new Set(documents.map((d) => d.id ?? d.dos)));
    };

    const clearFilters = () => {
        setSelectedPatientName("")
        setSearchText("");
        setStartDate("");
        setEndDate("");

        setPatientResults([]);
    };

    const handleSaveCoverage = () => {
        setSendStatus("Coverage sheet saved.");
        setTimeout(() => setSendStatus(""), 2500);
    };





    const handleSelectAllChange = () => {
        const allSelected = filteredDocuments.every((_, index) => selectedRows.includes(index));

        if (allSelected) {
            // Deselect all checkboxes
            setSelectedRows([]);
        } else {
            // Select all checkboxes
            setSelectedRows(Array.from({ length: filteredDocuments.length }, (_, index) => index));
        }
    };

    const handleRowCheckboxChange = (index, doc) => {
        setSelectedDocumentId(doc.DocumentID);
        setDocsData(doc);
        if (selectedRows.includes(index)) {
            setSelectedRows(selectedRows.filter((selectedRow) => selectedRow !== index));
        } else {
            setSelectedRows([...selectedRows, index]);
        }
    };



    const getAddressBook = async () => {
        try {

            const payload = {
                PracticeID: userPracticeID
            };

            const APIURL =
                `${APIConstants.SERVICES_URL}Fax/GetPracticeAddressBookDetails?sInputJSON=${encodeURIComponent(
                    JSON.stringify(payload)
                )}`;

            const response = await fetch(APIURL, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sAuthToken}`
                }
            });

            const data = await response.json();

            const parsed = JSON.parse(data);
            console.log("GetPracticeAddressBookDetails response:", parsed);
            setAddressBook(parsed);

        } catch (err) {
            console.log(err);
        }
    };



    const handleSaveContact = async () => {

        if (!contactName.trim()) {
            alert("Please enter Name.");
            return;
        }

        if (!contactFax.trim()) {
            alert("Please enter Fax Number.");
            return;
        }

        try {

            const payload = {
                cname: contactName,
                caddress: "",
                cemail: "",
                cphone: "",
                cfaxno: contactFax,
                cremark: "",
                PracticeID: userPracticeID
            };

            const response = await fetch(
                `${APIConstants.SERVICES_URL}Fax/InsertAddressBook?sInputJSON=${encodeURIComponent(
                    JSON.stringify(payload)
                )}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sAuthToken}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error("Failed to save contact.");
            }

            const result = await response.json();

            console.log(result);

            await getAddressBook();

            setContactName("");
            setContactFax("");
            setShowAddContact(false);

            alert("Contact saved successfully.");

        } catch (error) {
            console.error("Save Contact Error:", error);
            alert("Unable to save contact.");
        }
    };


    const getFolders = async () => {
        try {

            const input = {
                UserID: userId,
                PracticeID: userPracticeID,
                RoleID: userData.roleID,
                PatientID: selectedPatient
            };

            const url =
                `${APIConstants.SERVICES_URL}Folder/PatientFoldernadDoc?sInputJSON=${encodeURIComponent(
                    JSON.stringify(input)
                )}`;

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sAuthToken}`
                }
            });

            const data = await response.json();


            console.log("PatientFoldernadDoc response:", data);
            setFolders(data);

        } catch (err) {
            console.log(err);
        }
    };


    useEffect(() => {
        getFolders();
    }, [selectedPatient]);

    const saveFolder = async () => {

        try {

            const FolderInput = {
                FolderName: folderName,
                ParentFolderID: 0,
                UserID: String(userData.userID),
                PracticeID: String(userPracticeID),
                RoleID: String(userData.roleID),
                PatientID: String(selectedPatient),
                FromDOS: fromDOS,
                ToDOS: toDOS,
                IsImportFile: false,
                objDocType: []
            };

            const response = await fetch(
                `${APIConstants.SERVICES_URL}Folder/CreateFolder`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        Authorization: `Bearer ${sAuthToken}`
                    },

                    body: JSON.stringify(FolderInput)
                }
            );

            const result = await response.json();

            console.log(result);

            alert("Folder created successfully.");

            setShowFolderPopup(false);
            getFolders();
            setFolderName("");
            setFromDOS("");
            setToDOS("");

        } catch (err) {

            console.log(err);

        }

    };



    const handleExpand = (folderId) => {
        setExpandedFolder(expandedFolder == folderId ? null : folderId);

        if (!currentPage[folderId]) {
            setCurrentPage(prev => ({
                ...prev,
                [folderId]: 1
            }));
        }
    };


    const saveSubFolder = async () => {
        if (!folderName.trim()) {
            alert("Please enter folder name.");
            return;
        }

        try {
            const FolderInput = {
                FolderName: folderName,          // New sub-folder name entered by user
                ParentFolderID: parentFolderId,  // Selected parent folder

                UserID: String(userId),
                PracticeID: String(userPracticeID),
                RoleID: String(userData.roleID),
                PatientID: String(selectedPatient),

                FromDOS: fromDOS || "",
                ToDOS: toDOS || "",

                IsImportFile: false,
                objDocType: []
            };

            const response = await fetch(
                `${APIConstants.SERVICES_URL}Folder/CreateFolder`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sAuthToken}`
                    },

                    // Single stringify as requested
                    body: JSON.stringify(FolderInput)
                }
            );

            const result = await response.json();

            console.log(result);

            alert("Folder created successfully.");

            setShowSubFolderPopup(false);
            setFolderName("");


            getFolders();

        } catch (err) {
            console.log(err);
        }
    };


    const saveRenameFolder = async () => {
        if (!folderName.trim()) {
            alert("Please enter folder name.");
            return;
        }

        try {
            const FolderInput = {
                FolderID: parentFolderId,
                FolderName: folderName,          // New folder name entered by user
                UserID: String(userId),
                PracticeID: String(userPracticeID),
                RoleID: String(userData.roleID),
                PatientID: String(selectedPatient),
                FromDOS: '',
                ToDOS: ''

            };

            const response = await fetch(
                `${APIConstants.SERVICES_URL}Folder/RenameFolder`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sAuthToken}`
                    },

                    // Single stringify as requested
                    body: JSON.stringify(FolderInput)
                }
            );

            const result = await response.json();

            console.log(result);

            alert("Folder renamed successfully.");

            setShowRenameFolderPopup(false);
            setFolderName("");


            getFolders();

        } catch (err) {
            console.log(err);
        }
    };


    const deleteFolder = async (folderId) => {
        try {
            const FolderInput = {
                FolderID: folderId,
                FolderName: "",
                UserID: String(userId),
                PracticeID: String(userPracticeID),
                RoleID: String(userData.roleID),
                PatientID: String(selectedPatient),
                FromDOS: "",
                ToDOS: "",
                objDocType: []
            };

            const response = await fetch(
                `${APIConstants.SERVICES_URL}Folder/DeleteFolder`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sAuthToken}`,
                    },

                    // Single stringify
                    body: JSON.stringify(FolderInput),
                }
            );

            if (!response.ok) {
                throw new Error("Unable to delete folder.");
            }

            const data = await response.json();
            console.log(data);

            // Show success popup
            setSuccessMessage("Folder deleted successfully.");


            setTimeout(() => {
                setSuccessMessage("");
                getFolders();
            }, 2000);

        } catch (err) {
            console.error(err);
            alert("Unable to delete folder.");
        }
    };




    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.readAsDataURL(file);

            reader.onload = () => {
                // Remove the data:mime/type;base64, prefix
                const base64 = reader.result.split(",")[1];
                resolve(base64);
            };

            reader.onerror = (error) => reject(error);
        });
    };


    const handleFileUpload = async (e) => {
        const list = Array.from(e.target.files);

        if (!list.length) return;

        try {

            const items = [];

            for (const file of list) {

                items.push({
                    UserID: String(userId),
                    PracticeID: String(userPracticeID),
                    PatientID: String(selectedPatient),
                    FolderID: String(selectedFolderId),
                    Base64String: await fileToBase64(file),
                    FileName: file.name
                });

            }

            const response = await fetch(
                `${APIConstants.SERVICES_URL}Folder/UploadFiletoFolder`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sAuthToken}`,
                    },

                    // Single stringify
                    body: JSON.stringify(items),
                }
            );

            if (!response.ok) {
                throw new Error("Upload failed");
            }

            const result = await response.json();

            console.log(result);

            alert("Files uploaded successfully.");

            // Refresh folders/files
            getFolders();

        } catch (err) {

            console.error(err);

            alert("Unable to upload files.");

        } finally {


            e.target.value = "";

        }
    };


    const handleMoveFiles = async () => {

        try {

            const payload = selectedRows.map(() => ({

                DestFolderID: moveFolderId,

                DocumentID: selectedDocumentId,

                UserID: String(userId),

            }));

            const response = await fetch(
                `${APIConstants.SERVICES_URL}Folder/CopyFilestoFolder`,
                {
                    method: "POST",

                    headers: {

                        Accept: "application/json",

                        "Content-Type": "application/json",

                        Authorization: `Bearer ${sAuthToken}`

                    },

                    body: JSON.stringify(payload)

                }
            );

            if (!response.ok) {
                throw new Error("Move failed.");
            }

            await response.json();

            setShowMovePopup(false);

            setSelectedRows([]);

            setShowMoveSuccess(true);

            setTimeout(() => {

                setShowMoveSuccess(false);

                getFolders();

            }, 2000);

        } catch (err) {

            console.log(err);

        }

    };


    const deleteFile = async () => {

        try {

            const DeleteInput = {

                DocumentID: parseInt(selectedFile.documentId, 10) || 0,

                UserID: parseInt(userId, 10) || 0,

                FolderID: String(selectedFile.folderId || 0)

            };

            const response = await fetch(

                `${APIConstants.SERVICES_URL}Folder/DeleteFiles`,

                {

                    method: "DELETE",

                    headers: {

                        Accept: "application/json",

                        "Content-Type": "application/json",

                        Authorization: `Bearer ${sAuthToken}`

                    },

                    // Single stringify
                    body: JSON.stringify(DeleteInput)

                }

            );

            if (!response.ok) {

                throw new Error("Unable to delete file.");

            }

            await response.json();

            setShowDeleteFilePopup(false);

            setSelectedFile(null);

            setShowDeleteFileSuccess(true);

            setTimeout(() => {

                setShowDeleteFileSuccess(false);

                getFolders();

            }, 2000);

        }

        catch (err) {

            console.log(err);

        }

    };

    const formatDate = (date) => {
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
    };

    useEffect(() => {
        const today = new Date();

        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);

        setStartSentDate(formatDate(sevenDaysAgo));
        setendSentDate(formatDate(today));

    }, []);


    const getSentFaxes = async () => {

        setLoading(true);

        try {

            const FaxCenter = {
                PracticeID: userPracticeID,
                FromDate: startSentDate,
                ToDate: endSentDate
            };

            const url =
                `${APIConstants.SERVICES_URL}Fax/GetFaxOutbox?sInputJSON=${encodeURIComponent(
                    JSON.stringify(FaxCenter)
                )}`;

            const response = await fetch(url, {

                method: "GET",

                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sAuthToken}`
                }

            });

            if (!response.ok) {
                throw new Error("Unable to fetch sent faxes");
            }

            const data = await response.json();
            const parseData = JSON.parse(data)

            //console.log("GetFaxOutbox response:", parseData);

            setSentFaxes(parseData);

        } catch (err) {

            console.log(err);

        } finally {

            setLoading(false);

        }

    };


    useEffect(() => {
        getSentFaxes();
    }, []);




    const parseDate = (dateStr) => {
        if (!dateStr) return null;

        const [month, day, year] = dateStr.split("/").map(Number);
        return new Date(year, month - 1, day);
    };

    const displayDocuments = useMemo(() => {

        // First show search + sorted data only
        if (!isDateFilterApplied) {
            return filteredDocuments;
        }


        if (!startDate || !endDate) {
            return filteredDocuments;
        }

        const from = parseDate(startDate);
        from.setHours(0, 0, 0, 0);

        const to = parseDate(endDate);
        to.setHours(23, 59, 59, 999);

        return filteredDocuments.filter((doc) => {
            const docDate = parseDate(doc.DocDate);

            return (
                docDate &&
                docDate >= from &&
                docDate <= to
            );
        });

    }, [filteredDocuments, startDate, endDate, isDateFilterApplied]);

    const handleFilter = () => {

        if (!startDate || !endDate) {
            setIsDateFilterApplied(false);
            return;
        }

        setIsDateFilterApplied(true);

    };

    const handleRegenerate = async () => {

        if (selectedRows.length == 0) {
            alert("Please select at least one file.");
            return;
        }

        setIsRegenerating(true);

        try {

            const documentDetails = selectedRows.map(index => {
                const item = displayDocuments[index];

                return {
                    DocumentName: item.DocumentName,
                    DocType: String(item.PatDocType) || "",
                    DOS: item.DocDate,
                    FormID: String(item.FormID),
                    UserID: String(item.UploadedUserID) || "",
                    SupervisorID: String(item.SupervisorUserID) || "",
                    supervisorSignOffDate: item.SupervisorsignOffDate || ""
                };
            });

            const payload = {
                PatientID: String(selectedPatient),
                PracticeID: String(userPracticeID),
                DocumentDetails: documentDetails,
                FormID: ""
            };

            const response = await fetch(
                APIConstants.BASE_URL_PROVIDER_MS + "RegenerateAPI/MarkForReparseAndRegenerate",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sAuthToken}`
                    },
                    body: JSON.stringify(payload)   // single stringify
                }
            );

            const result = await response.json();

            alert(`Files (${selectedRows.length}) are processing.`);

            setSelectedRows([]);

        } catch (err) {
            console.error(err);
            alert("Something went wrong.");
        } finally {
            setIsRegenerating(false);
        }
    };


    const handleDownload = async () => {

        if (selectedRows.length == 0) {
            alert("Please select at least one file.");
            return;
        }

        try {

            const fileArray = selectedRows.map(index => {
                const item = displayDocuments[index];
                return {
                    FileName: item.DocumentName,
                    FilePhyPath: item.DocPhyPath,
                    DocWebPath: item.DocWebPath
                };
            });

            const payload = {
                FileArray: fileArray,
                PatientName: selectedPatientName
            };

            const response = await fetch(
                `${APIConstants.SERVICES_URL}DataExport/DownLoadFiles`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sAuthToken}`
                    },
                    // Double stringify
                    body: JSON.stringify(JSON.stringify(payload))
                }
            );

            if (!response.ok) {
                throw new Error("Download failed");
            }


            const url = await response.text();
            console.log("DOWNLOAD FILES RES:", url)
            //const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `${selectedPatientName}.zip`;
            document.body.appendChild(a);
            a.click();

            a.remove();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error(error);
            alert("Failed to download files.");
        }
    };



    const fileBase64 = (file) =>
        new Promise((resolve, reject) => {

            const reader = new FileReader();

            reader.readAsDataURL(file);

            reader.onload = () => {

                const base64 = reader.result.split(",")[1];

                resolve(base64);

            };

            reader.onerror = (error) => reject(error);

        });


    const getBase64FromUrl = async (url) => {
        const response = await fetch(url);

        const blob = await response.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => {
                resolve(reader.result.split(",")[1]);
            };

            reader.onerror = reject;

            reader.readAsDataURL(blob);
        });
    };


    const handleUploadFiles = async (e) => {
        const localFiles = Array.from(e.target.files || []);

        const mergeFiles = Array.isArray(selectedMergeFiles)
            ? selectedMergeFiles
            : Object.values(selectedMergeFiles || {});

        const allFiles = [...localFiles, ...mergeFiles];

        if (!allFiles.length) {
            alert("Please select at least one file.");
            return;
        }

        setIsUploading(true);

        try {
            const uploadFiles = await Promise.all(
                allFiles.map(async (file) => {
                    try {
                        // Case 1: Local file (from <input type="file">)
                        if (file instanceof File) {
                            return {
                                Base64String: await fileBase64(file),
                                FileName: file.name,
                            };
                        }


                        if (file) {
                            return {
                                Base64String: await getBase64FromUrl(file.FileWebPath),
                                FileName: file.DocumentName,
                            };
                        }

                        // Case 3: Merge file as array [_, name, _, url]
                        if (Array.isArray(file)) {
                            if (!file[3]) {
                                console.warn("Merge file array missing URL at index 3:", file);
                                return null;
                            }
                            return {
                                Base64String: await getBase64FromUrl(file[3]),
                                FileName: file[1] || "file",
                            };
                        }

                        console.warn("Unrecognized file shape, skipping:", file);
                        return null;
                    } catch (fileErr) {
                        console.error("Failed to process file:", file, fileErr);
                        return null;
                    }
                })
            );

            const validFiles = uploadFiles.filter(Boolean);

            if (!validFiles.length) {
                alert("None of the selected files could be processed.");
                return;
            }

            const response = await fetch(
                `${APIConstants.SERVICES_URL}MiscellaneousServices/UploadTempFiles`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sAuthToken}`,
                    },
                    body: JSON.stringify(validFiles),
                }
            );

            if (!response.ok) {
                throw new Error(`Upload failed with status ${response.status}`);
            }

            const result = await response.json();
            console.log("RESULT OF UPLOAD FILE", result);

            if (result) {
                setUploadedFiles(result);

            }
        } catch (err) {
            console.error(err);
            alert("Upload failed.");
        } finally {
            setIsUploading(false);
            e.target.value = "";
        }
    };


    const handleSendFax = async () => {


        try {

            const SelectedDocs = (selectedMergeFiles || []).map(item => ({
                DocumentName: item.DocumentName,
                DocumentPhyPath: item.FilePhyPath,
                DocuemntWebPath: item.FileWebPath,
                DocumentType: 0
            }));

            const MisFiles = await Promise.all(
                uploadedFiles.map(async (file) => ({
                    DocumentPath: file.phyPath,
                    DocumentName: file.originalName,
                    CoverPage: Boolean(isSelectedchecked),
                    DocumentBase64: await getBase64FromUrl(file.webPath),
                }))
            );

            const APIInput = {

                PracticeID: String(userPracticeID),
                UserID: String(userId),
                SenderName: senderFrom,
                SenderNumber: senderPhone,
                PatientName: selectedPatientName,
                ReceiverNumber: destinationFax,
                CoverPageText: coverageMessage,
                ReceiverName: recipientTo,
                CoverPageName: coverageName,
                lstFaxDocuements: SelectedDocs,
                lstMiscFileBase64: MisFiles,
            };

            const response = await fetch(
                APIConstants.SERVICES_URL + "Fax/FaxDocuments",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sAuthToken}`
                    },
                    body: JSON.stringify(APIInput)
                }
            );

            if (!response.ok) {
                throw new Error("Failed to send fax");
            }

            const result = await response.json();

            alert("Fax sent successfully.");

        } catch (error) {
            console.error(error);
            alert("Unable to send fax.");
        }
    };



    const handleSelect95Files = () => {

        if (!expandedFolder) {
            alert("Open a folder to select its file.");
            return;
        }

        const folder = folders.find(
            f => f.Id == expandedFolder
        );

        if (!folder) return;

        const allFileIds = folder.Children
            .slice(0, 95)
            .map(file => file.DocumentID);

        const alreadySelected =
            selectedFiles[expandedFolder]?.length == allFileIds.length;

        if (alreadySelected) {

            setSelectedFiles(prev => ({
                ...prev,
                [expandedFolder]: []
            }));

        } else {

            setSelectedFiles(prev => ({
                ...prev,
                [expandedFolder]: allFileIds
            }));
        }
    };


    const isCurrentFolderSelected =
        expandedFolder &&
        selectedFiles[expandedFolder]?.length > 0;


    const handleFileCheckbox = (file) => {
        setSelectedMergeFiles(file)
        if (!expandedFolder) return;

        setSelectedFiles(prev => {

            const currentSelected = prev[expandedFolder] || [];

            const isSelected = currentSelected.includes(file.DocumentID);

            return {
                ...prev,
                [expandedFolder]: isSelected
                    ? currentSelected.filter(id => id !== file.DocumentID)
                    : [...currentSelected, file.DocumentID]
            };
        });
    };



    const handleSelectAll = () => {

        const allIds = mergedFiles.map(file => file.DocumentID);

        setSelectedFiles(prev => {

            const current = prev[expandedFolder] || [];

            return {
                ...prev,
                [expandedFolder]:
                    current.length === allIds.length ? [] : allIds
            };

        });

    };


    const handleMergedFileCheckbox = (documentId, file) => {
        setSelectedMergeFiles(prev => {
            const files = Array.isArray(prev) ? prev : [];

            const exists = files.some(f => f.DocumentID === documentId);

            if (exists) {
                return files.filter(f => f.DocumentID !== documentId);
            }

            return [...files, file];
        });

        setSelectedFiles(prev => {
            const current = prev[expandedFolder] || [];

            const updated = current.includes(documentId)
                ? current.filter(id => id !== documentId)
                : [...current, documentId];

            return {
                ...prev,
                [expandedFolder]: updated
            };
        });
    };


    const handleSearch = async () => {

        if (!searchKey.trim()) {
            alert("Please enter a search value.");
            return;
        }

        try {

            const payload = {
                SearchKey: searchKey,
                PracticeID: userPracticeID
            };

            const api =
                searchType === "claimID"
                    ? "Search/PatientByClaimID"
                    : "Search/PatientByPolicyNumber";

            const response = await fetch(
                `${APIConstants.BASE_URL_SEARCH}${api}?sInputJSON=${encodeURIComponent(
                    JSON.stringify(payload)
                )}`,
                {
                    method: "GET", // Change to POST if your API requires POST
                    headers: {
                        Authorization: `Bearer ${sAuthToken}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error("Search failed.");
            }

            const data = await response.json();
            console.log("Search PATient", data)
            setSelectedPatient(data.patientID)
            setSearchResult(data);

        } catch (error) {
            console.error(error);
            alert("Unable to search.");
        }
    };


    const getMergedFiles = async () => {
        setTimeout(() => {
            setShowMergeSuccess(false);
        }, 3000);

        const payload = {

            UserID: userId,

            PatientID: selectedPatient,

            PracticeID: userPracticeID

        };

        const response = await fetch(
            `${APIConstants.SERVICES_URL}Folder/MergedFiles?sInputJSON=${encodeURIComponent(
                JSON.stringify(payload)
            )}`,

            {

                method: "GET",

                headers: {

                    Authorization: `Bearer ${sAuthToken}`

                }

            }

        );

        const data = await response.json();
        //console.log("MERGED DATA", data)

        setMergedFiles(data);

    }

    const handleMergePDF = () => {

        const files =
            selectedFiles[expandedFolder] || [];

        if (files.length < 2) {
            setShowMergeValidation(true);
            setTimeout(() => {
                setShowMergeValidation(false);
            }, 2000);

            return;
        }

        const selectedIds = selectedFiles[expandedFolder] || [];

        const selectedDocs = documents.filter(doc =>
            selectedIds.includes(doc.DocumentID)
        );

        setSelectedMergeFiles(selectedDocs);

        setShowMergeConfirm(true);
    };




    const handleconfirmMerge = async () => {
       // console.log("FILE MERGED", selectedMergeFiles)

        setShowMergeConfirm(false);

        setShowMergeProgress(true);

        try {

            const payload = await Promise.all(

                selectedMergeFiles.map(async (file) => ({

                    UserID: String(userId),

                    PatientID: String(selectedPatient),

                    FolderID: expandedFolder,

                    Base64String: await getBase64FromUrl(file.DocWebPath),

                    FileName: file.DocumentName,

                    PracticeID: String(userPracticeID)

                }))

            );

            await fetch(
                APIConstants.SERVICES_URL + "Folder/UploadeMergedFiles",


                {

                    method: "POST",

                    headers: {

                        Authorization: `Bearer ${sAuthToken}`,

                        "Content-Type": "application/json"

                    },

                    body: JSON.stringify(payload)

                }

            );

            await getMergedFiles();

        }

        finally {

            setShowMergeProgress(false);

            setShowMergeSuccess(true);

        }

    }



    useEffect(() => {
        handleconfirmMerge()

    }, [selectedPatient]);



    return (
        <div className="hv-page">
            <div className="hv-container">
                <header className="hv-pagehead">
                    <h1 className="hv-pagehead__title">Fax Documents</h1>
                </header>

                <nav className="hv-tabbar" role="tablist" aria-label="Fax Documents">
                    {[
                        { key: "new", label: "New" },
                        { key: "sent", label: "Sent" },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`hv-tabbar__tab${activeTab === tab.key ? ' is-active' : ''}`}
                        >
                            {tab.label} ⟳
                        </button>
                    ))}
                </nav>

            {activeTab === "new" && (

                <div className="hv-row hv-row--top">

                    <div className="hv-panel hv-grow hv-min-0">
                        <div className="hv-panel__head">
                            <h2 className="hv-panel__heading">User file(s) Upload Information</h2>
                        </div>
                        <div className="hv-panel__head">
                            <span className="hv-field__label">Find Patient By:</span>
                            <select
                                className="hv-select hv-select--sm"
                                value={searchType}
                                onChange={(e) => setSearchType(e.target.value)}
                            >
                                <option value="claimID">Claim ID</option>
                                <option value="policyID">Policy ID</option>
                            </select>

                            <input
                                type="text"
                                placeholder={
                                    searchType === "claimID"
                                        ? "Enter Claim ID..."
                                        : "Enter Policy ID..."
                                }
                                className="hv-input hv-input--sm"
                                style={{ maxWidth: 200 }}
                                value={searchKey}
                                onChange={(e) => setSearchKey(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        handleSearch();
                                    }
                                }}
                            />

                            <button type="button" className="hv-icon-btn hv-icon-btn--sm" title="Search" onClick={handleSearch}>
                                <Search size={14} />
                            </button>
                        </div>

                        <div className="hv-panel__head">
                            <span className="hv-field__label">Patient Name:</span>
                            <div className="hv-search hv-search--sm" style={{ flex: '0 1 220px' }}>
                                <input
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={handleInputKeyPress}
                                    placeholder="Search patient…"
                                />

                                {suggestions.length > 0 && (
                                    <div className="hv-panel" style={{ position: 'absolute', zIndex: 20, marginTop: 4, width: 256, maxHeight: 224, overflowY: 'auto' }}>
                                        {suggestions.map((suggestion) => (
                                            <div
                                                key={suggestion.PatientID}
                                                className="hv-td-link"
                                                style={{ display: 'block', padding: '6px 12px' }}
                                                onClick={() => handleSuggestionSelect(suggestion)}
                                            >
                                                {suggestion.LastName}, {suggestion.FirstName}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {selectedPatientName && (
                                <span className="hv-chip">
                                    {selectedPatientName}
                                </span>
                            )}

                            <div className="hv-field hv-field--inline">
                                <label className="hv-field__label">From Date:</label>
                                <DatePicker
                                    selected={startDate ? new Date(startDate) : null}
                                    onChange={handleStartDateChange}
                                    dateFormat="MM/dd/yyyy"
                                    maxDate={new Date()}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    yearDropdownItemNumber={100}
                                    className="hv-date"
                                    placeholderText="MM/dd/yyyy"
                                    calendarContainer={makeCalContainer("start")}
                                    open={isStartOpen}
                                    onFocus={() => setIsStartOpen(true)}
                                    onInputClick={() => setIsStartOpen(true)}
                                    onClickOutside={() => setIsStartOpen(false)}
                                    renderCustomHeader={renderCalendarHeader}
                                />
                            </div>

                            <div className="hv-field hv-field--inline">
                                <label className="hv-field__label">To Date:</label>
                                <DatePicker
                                    selected={endDate ? new Date(endDate) : null}
                                    onChange={handleEndDateChange}
                                    dateFormat="MM/dd/yyyy"
                                    maxDate={new Date()}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    yearDropdownItemNumber={100}
                                    className="hv-date"
                                    placeholderText="MM/dd/yyyy"
                                    popperPlacement="bottom-start"
                                    calendarContainer={makeCalContainer("end")}
                                    open={isEndOpen}
                                    onFocus={() => setIsEndOpen(true)}
                                    onInputClick={() => setIsEndOpen(true)}
                                    onClickOutside={() => setIsEndOpen(false)}
                                    renderCustomHeader={renderCalendarHeader}
                                />
                            </div>


                            <button type="button" className="hv-btn hv-btn--sm" onClick={handleFilter}>
                                <FilterIcon size={13} /> Filter
                            </button>
                            <button type="button" className="hv-btn hv-btn--ghost hv-btn--sm" onClick={clearFilters}>
                                <X size={13} /> Clear
                            </button>
                        </div>

                        {/* Documents table */}
                        <div className="hv-table-scroll">
                            <table className="hv-table">
                                <thead>
                                    <tr>
                                        <th className="hv-th-check">
                                            #
                                        </th>
                                        {COLUMNS.map((c) => (
                                            <th key={c.key} onClick={() => handleSort(c.key)} className="is-sortable">
                                                <SortableHeader label={c.label} />
                                            </th>
                                        ))}
                                        <th>Select
                                            <input
                                                className="hv-th-check"
                                                type='checkbox'
                                                onChange={() => handleSelectAllChange('all')}
                                            />
                                        </th>
                                    </tr>
                                    <tr className="hv-table__searchrow">
                                        <th></th>
                                        {COLUMNS.map((c) => (
                                            <th key={c.key}>
                                                <input
                                                    value={filters[c.key]}
                                                    onChange={(e) => handleFilterChange(c.key, e.target.value)}
                                                    placeholder={c.filterLabel}
                                                />
                                            </th>
                                        ))}
                                        <th className="hv-t-center">
                                            <button type="button" className="hv-icon-btn hv-icon-btn--sm" title="Regenerate"
                                                onClick={handleRegenerate}
                                                disabled={isRegenerating}
                                            >
                                                <RefreshCw size={14} className={isRegenerating ? "spin-icon" : ""} />
                                            </button>
                                            <button type="button" className="hv-icon-btn hv-icon-btn--sm" title="Download" onClick={handleDownload}>
                                                <Download size={14} />
                                            </button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {docsLoading && (
                                        <tr>
                                            <td colSpan={COLUMNS.length + 2} className="hv-t-center hv-t-faint">
                                                Loading documents…
                                            </td>
                                        </tr>
                                    )}
                                    {!docsLoading && documents.length === 0 && (
                                        <tr>
                                            <td colSpan={COLUMNS.length + 2} className="hv-t-center hv-t-faint">
                                                No documents found
                                            </td>
                                        </tr>
                                    )}
                                    {!docsLoading &&
                                        displayDocuments.map((doc, idx) => {
                                            const id = doc.id ?? doc.dos ?? idx;

                                            return (
                                                <tr
                                                    key={id}
                                                    className={selectedRows.includes(idx) ? "is-selected" : ""}
                                                >
                                                    <td>
                                                        {idx + 1}
                                                    </td>
                                                    <td className="hv-td-strong hv-t-teal">{doc.DocDate}</td>
                                                    <td>{doc.DocTypeName}</td>
                                                    <td>{doc.ClaimID}</td>
                                                    <td>{doc.CPTCode}</td>
                                                    <td>{doc.PayerRefNumber}</td>
                                                    <td className="hv-t-green">{doc.FirstName} {doc.LastName}</td>
                                                    <td className="hv-t-green">{formatDOSDate(doc.SupervisorsignOffDate)}</td>
                                                    <td className="hv-td-strong hv-t-amber">{doc.DocNumberOfPages}</td>
                                                    <td>
                                                        <div className="hv-row hv-row--tight">
                                                            <input
                                                                type='checkbox'
                                                                key={idx}
                                                                checked={selectedRows.includes(idx, doc)}
                                                                onChange={() => handleRowCheckboxChange(idx, doc)}
                                                            />

                                                            <FontAwesomeIcon className="hv-t-red" style={{ cursor: 'pointer' }} icon={faFilePdf}
                                                                onClick={() => {
                                                                    setPdfUrl(doc.DocWebPath);
                                                                    setShowPdfPopup(true);
                                                                }}
                                                            />
                                                            {selectedRows.includes(idx) && (
                                                                <FontAwesomeIcon
                                                                    icon={faCircleCheck}
                                                                    className="success-icon hv-t-green"
                                                                />
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                            {usingMockDocs && !docsLoading && documents.length > 0 && (
                                <div className="hv-notice hv-notice--pending">
                                    Showing sample data — live document endpoint not reachable from this preview
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ================= RIGHT: coverage / sender / recipient / files ================= */}
                    <div className="hv-stack hv-grow hv-min-0">
                        <Section title="CoverPage Information">
                            <Field label="Name:">
                                <input
                                    className={inputCls}
                                    value={coverageName}
                                    onChange={(e) => setCoverageName(e.target.value)}
                                />
                            </Field>
                            <Field label="Message:">
                                <textarea
                                    rows={3}
                                    className={inputCls}
                                    value={coverageMessage}
                                    onChange={(e) => setCoverageMessage(e.target.value)}
                                />
                            </Field>
                        </Section>

                        <Section title={<span className="hv-inline">Sender Information <CheckCircle2 size={14} className="hv-t-green" /></span>}>
                            <Field label="From :">
                                <input className={inputCls} value={senderFrom} onChange={(e) => setSenderFrom(e.target.value)} />
                            </Field>
                            <Field label="Phone No :">
                                <input className={inputCls} value={senderPhone} onChange={(e) => setSenderPhone(e.target.value)} />
                            </Field>
                            <Field label="Fax No :">
                                <input className={inputCls} value={senderFax} onChange={(e) => setSenderFax(e.target.value)} />
                            </Field>

                        </Section>

                        <Section title="Recipient Information">
                            <Field label="To :">
                                <input className={inputCls} value={recipientTo} onChange={(e) => setRecipientTo(e.target.value)} />
                            </Field>
                            <Field label="Destination Fax # :">

                                <div style={{ position: 'relative', width: '100%' }}>
                                    <input
                                        className="hv-input hv-input--icon-right"
                                        placeholder="Enter your fax number"
                                        value={destinationFax}
                                        onChange={(e) => setDestinationFax(e.target.value)}
                                    />

                                    <BookUser
                                        size={18}
                                        onClick={() => {
                                            setShowAddressBook(true);
                                            getAddressBook();
                                        }}
                                        className="hv-t-teal"
                                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                                    />
                                </div>

                            </Field>

                            {showAddressBook && (
                                <>
                                    <div className="hv-scrim" onClick={() => setShowAddressBook(false)} />
                                    <div className="hv-modal">
                                        <div className="hv-modal__box" style={{ width: 'min(520px, 100%)', maxHeight: 'min(600px, calc(100vh - 80px))' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">Address Book</span>
                                                <button type="button" className="hv-icon-btn hv-icon-btn--sm" onClick={() => setShowAddContact(!showAddContact)} title="Add contact">
                                                    <Plus size={15} />
                                                </button>
                                                <button type="button" className="hv-modal__close" onClick={() => setShowAddressBook(false)}>
                                                    <X size={18} />
                                                </button>
                                            </div>

                                            <div className="hv-modal__body">
                                                <div className="hv-search hv-search--full hv-mb-5">
                                                    <input
                                                        placeholder="Search name or fax number..."
                                                        value={searchContact}
                                                        onChange={(e) => setSearchContact(e.target.value)}
                                                    />
                                                </div>

                                                {showAddContact && (
                                                    <div className="hv-stack hv-stack--tight" style={{ marginBottom: 16 }}>
                                                        <input
                                                            type="text"
                                                            placeholder="Name"
                                                            className="hv-input"
                                                            value={contactName}
                                                            onChange={(e) => setContactName(e.target.value)}
                                                        />

                                                        <input
                                                            type="text"
                                                            placeholder="Fax number"
                                                            className="hv-input"
                                                            value={contactFax}
                                                            onChange={(e) => setContactFax(e.target.value)}
                                                        />

                                                        <div className="hv-row">
                                                            <button type="button" className="hv-btn hv-btn--block" onClick={handleSaveContact}>
                                                                Save
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="hv-btn hv-btn--ghost hv-btn--block"
                                                                onClick={() => {
                                                                    setShowAddContact(false);
                                                                    setContactName("");
                                                                    setContactFax("");
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="hv-table-scroll">
                                                    <table className="hv-table">
                                                        <thead>
                                                            <tr>
                                                                <th className="hv-th-group--brand">#</th>
                                                                <th className="hv-th-group--brand">NAME</th>
                                                                <th className="hv-th-group--brand">FAX NUMBER</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {addressBook
                                                                .filter((item) => {
                                                                    const name = (item.cname || "").toLowerCase();
                                                                    const fax = (item.cfaxno || "").toString();

                                                                    return (
                                                                        name.includes(searchContact.toLowerCase()) ||
                                                                        fax.includes(searchContact)
                                                                    );
                                                                })
                                                                .map((item, index) => (
                                                                    <tr
                                                                        key={index}
                                                                        className="is-clickable"
                                                                        onClick={() => {
                                                                            setRecipientTo(item.cname || "");
                                                                            setDestinationFax(item.cfaxno || "");
                                                                            setShowAddressBook(false);
                                                                        }}
                                                                    >
                                                                        <td>{index + 1}</td>

                                                                        <td className="hv-td-strong hv-t-teal">
                                                                            {item.cname}
                                                                        </td>

                                                                        <td>
                                                                            {item.cfaxno}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="hv-row" style={{ justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setRecipientTo("");
                                        setDestinationFax("");
                                    }}
                                    className="hv-btn hv-btn--ghost hv-btn--sm"
                                >
                                    Cancel
                                </button>
                                <button type="button" onClick={handleSendFax} className="hv-btn hv-btn--sm">
                                    <Send size={13} /> Send Fax
                                </button>
                            </div>
                            {sendStatus && (
                                <div className="hv-notice hv-notice--info hv-mt-1">
                                    {sendStatus}
                                </div>
                            )}
                        </Section>

                        {/* Select files */}
                        <div className="hv-panel">
                            <div className="hv-panel__head">
                                <h2 className="hv-panel__heading">Select files</h2>
                            </div>

                            <div className="hv-panel__head">
                                <input
                                    className="hv-input"
                                    type="file"
                                    id="formFileMultiple"
                                    multiple
                                    style={{ maxWidth: 300 }}
                                    onChange={handleUploadFiles}
                                />
                            </div>

                            <div className="hv-table-scroll">
                                {Array.isArray(uploadedFiles) && uploadedFiles.length > 0 ? (
                                    <table className="hv-table">
                                        <thead>
                                            <tr>
                                                <th className="hv-th-group--brand"></th>
                                                <th className="hv-th-group--brand">#</th>
                                                <th className="hv-th-group--brand">File Name</th>
                                                <th className="hv-th-group--brand">View</th>
                                                <th className="hv-th-group--brand">Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {uploadedFiles.map((file, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <input
                                                            type="radio"
                                                            name="uploadedFile"
                                                            checked={isSelectedchecked === index}
                                                            onChange={() => setIsSelectedchecked(index)}
                                                        />
                                                    </td>
                                                    <td>{index + 1}</td>
                                                    <td>{file.originalName}</td>
                                                    <td>
                                                        <FontAwesomeIcon className="hv-t-red" style={{ cursor: 'pointer' }} icon={faFilePdf}
                                                            onClick={() => {
                                                                setPdfUrl(file.webPath);
                                                                setShowPdfPopup(true);
                                                            }}
                                                        />
                                                    </td>
                                                    <td><Trash2
                                                        size={16}
                                                        className="hv-t-red"
                                                        style={{ cursor: 'pointer' }}
                                                    /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="hv-empty hv-empty--sm">
                                        <span className="hv-t-xs">
                                            {selectedRows.size > 0
                                                ? `${selectedRows.size} document(s) selected from the table`
                                                : "Select documents from the table to fax them"}
                                        </span>
                                    </div>
                                )}

                                {usingMockDocs && !docsLoading && documents.length > 0 && (
                                    <div className="hv-notice hv-notice--pending">
                                        Showing sample data — live document endpoint not reachable from this preview
                                    </div>
                                )}
                            </div>


                            <div className="hv-panel__head">
                                <h2 className="hv-panel__heading">Merged files</h2>
                            </div>
                            <div style={{ margin: 12 }}>

                                {Array.isArray(mergedFiles) && mergedFiles.length > 0 ? (

                                    /* Show Folder UI */

                                    <div className="hv-panel hv-panel--flush">

                                        {/* Folder Header */}

                                        <div className="hv-panel__head">

                                            <div className="hv-row hv-row--tight">

                                                <span className="hv-t-center">

                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            mergedFiles.length > 0 &&
                                                            selectedFiles.length === mergedFiles.length
                                                        }
                                                        onChange={handleSelectAll}
                                                    />

                                                </span>

                                                <button
                                                    type="button"
                                                    className="hv-icon-btn hv-icon-btn--sm hv-icon-btn--bare"
                                                    onClick={() => setMergedFolderOpen(!mergedFolderOpen)}
                                                >
                                                    {mergedFolderOpen ? (
                                                        <ChevronDown size={18} className="hv-t-red" />
                                                    ) : (
                                                        <ChevronRight size={18} className="hv-t-red" />
                                                    )}
                                                </button>

                                                <FolderOpen
                                                    size={22}
                                                    className="hv-t-amber"
                                                />

                                                <div>

                                                    <div className="hv-t-bold hv-t-red">
                                                        All Merged Files
                                                    </div>

                                                    <div className="hv-t-xs hv-t-faint">
                                                        Merged PDF Documents
                                                    </div>

                                                </div>

                                            </div>

                                            <div className="hv-row hv-row--tight">

                                                <button
                                                    type="button"
                                                    className="hv-btn hv-btn--sm"
                                                    onClick={handleUploadFiles}
                                                >
                                                    Fax Selected
                                                </button>

                                                <span className="hv-pill hv-pill--info">
                                                    {mergedFiles.length} files
                                                </span>

                                            </div>

                                        </div>

                                        {mergedFolderOpen && (
                                            <div className="hv-table-scroll">
                                                <table className="hv-table">
                                                    <tbody>
                                                        {mergedFiles.map((file, index) => (
                                                            <tr key={file.DocumentID}>

                                                                <td className="hv-t-center">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={
                                                                            (selectedFiles[expandedFolder] || []).includes(file.DocumentID)
                                                                        }
                                                                        onChange={() => handleMergedFileCheckbox(file.DocumentID, file)}
                                                                    />
                                                                </td>

                                                                <td className="hv-t-center">
                                                                    <span className="hv-pill hv-pill--info">
                                                                        {index + 1}
                                                                    </span>
                                                                </td>

                                                                <td>
                                                                    <span className="hv-pill hv-pill--pending">
                                                                        MERGED
                                                                    </span>
                                                                </td>

                                                                <td>
                                                                    <div>
                                                                        <div className="hv-td-strong">
                                                                            {file.DocumentName}
                                                                        </div>

                                                                        <div className="hv-t-xs hv-t-faint">
                                                                            Created from :
                                                                            {" "}
                                                                            {file.SourceFolder}
                                                                            {" | "}
                                                                            {file.TotalMergedFiles} files merged
                                                                            {" | "}
                                                                            {file.CreatedDate}
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                <td className="hv-t-center">
                                                                    <button type="button" className="hv-btn hv-btn--soft hv-btn--sm" onClick={() => {
                                                                        setPdfUrl(file.FileWebPath);
                                                                        setShowPdfPopup(true);
                                                                    }}>
                                                                        View
                                                                    </button>
                                                                </td>

                                                                <td className="hv-t-center">
                                                                    <button type="button" className="hv-btn hv-btn--soft hv-btn--sm">
                                                                        Download
                                                                    </button>
                                                                </td>

                                                                <td className="hv-t-center">
                                                                    <button
                                                                        type="button"
                                                                        className="hv-btn hv-btn--sm"
                                                                        onClick={handleUploadFiles}
                                                                    >
                                                                        Fax
                                                                    </button>
                                                                </td>

                                                                <td className="hv-t-center">
                                                                    <Trash2
                                                                        size={15}
                                                                        className="hv-t-red"
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => {
                                                                            setSelectedFile({
                                                                                documentId: file.DocumentID,
                                                                            });

                                                                            setShowDeleteFilePopup(true);
                                                                        }}
                                                                    />
                                                                </td>

                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                    </div>

                                ) : (

                                    <div className="hv-empty hv-empty--sm">
                                        <span className="hv-t-sm">
                                            No merged files yet. Select at least two PDFs in a folder and click
                                            <span className="hv-t-bold hv-t-amber">
                                                {" "}Merge to PDF
                                            </span>.
                                        </span>
                                    </div>

                                )}

                            </div>


                        </div>

                        {/* User folder */}
                        <div className="hv-panel">
                            <div className="hv-panel__head">
                                <h2 className="hv-panel__heading" onClick={getFolders}>User folder</h2>
                            </div>
                            <div className="hv-row" style={{ padding: '10px 14px' }}>
                                <button
                                    type="button"
                                    className="hv-btn hv-btn--sm"
                                    onClick={() => setShowFolderPopup(true)}
                                >
                                    <FolderPlus size={13} /> New Folder
                                </button>
                                <button
                                    type="button"
                                    className="hv-btn hv-btn--sm"
                                    onClick={handleSelect95Files}
                                >
                                    <Files size={13} />
                                    {isCurrentFolderSelected
                                        ? "Unselect Files"
                                        : "Select 95 Files"}
                                </button>

                            </div>
                            <div className="hv-panel__body" style={{ paddingTop: 0 }}>
                                <div className="hv-tree">

                                    {Array.isArray(folders) &&
                                        folders.map(folder => {

                                            const searchValue = (folderSearch[folder.Id] || "").trim().toLowerCase();
                                            const childFolders = (folder.Children || []).filter(
                                                item => item.Type === "folder"
                                            );

                                            const files = (folder.Children || []).filter((file) =>
                                                (file.DocumentDescription || "")
                                                    .toLowerCase()
                                                    .includes(searchValue)
                                            );
                                            const current = currentPage[folder.Id] || 1;
                                            const totalPages = Math.ceil(files.length / itemsPerPage);

                                            const paginatedFiles =
                                                current === "all"
                                                    ? files
                                                    : files.slice(
                                                        (current - 1) * itemsPerPage,
                                                        current * itemsPerPage
                                                    );


                                            return (

                                                <>

                                                    <div
                                                        key={folder.Id}
                                                        className="hv-tree__folder"
                                                    >

                                                        <div className="hv-tree__row">

                                                            <div className="hv-tree__label">

                                                                <button
                                                                    type="button"
                                                                    className="hv-tree__toggle"
                                                                    onClick={() => handleExpand(folder.Id)}
                                                                >

                                                                    {
                                                                        expandedFolder == folder.Id ?

                                                                            <ChevronDown size={18} />

                                                                            :

                                                                            <ChevronRight size={18} />

                                                                    }

                                                                </button>

                                                                <Folder className="hv-t-amber" />

                                                                <div>

                                                                    <div className="hv-tree__name">

                                                                        {folder.Name}

                                                                    </div>

                                                                    <div className="hv-tree__meta">

                                                                        {folder.FirstName} {folder.LastName}

                                                                        &nbsp;

                                                                        Created :

                                                                        {folder.CreatedDate}

                                                                    </div>

                                                                </div>

                                                            </div>

                                                            <div className="hv-tree__actions">

                                                                <Plus
                                                                    size={17}
                                                                    className="hv-t-teal"
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => {

                                                                        setParentFolderId(folder.Id);
                                                                        setFolderName("");
                                                                        setParentFolderName(folder.Name);
                                                                        setShowSubFolderPopup(true);

                                                                    }}
                                                                />

                                                                <Pencil size={15}
                                                                    className="hv-t-teal"
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => {

                                                                        setParentFolderId(folder.Id);
                                                                        setFolderName(folder.Name);
                                                                        setShowRenameFolderPopup(true);

                                                                    }}
                                                                />

                                                                <Trash2 size={15}
                                                                    className="hv-t-red"
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => {
                                                                        setParentFolderId(folder.Id);
                                                                        setShowDeletePopup(true);
                                                                    }}
                                                                />

                                                                <button type="button" className="hv-btn hv-btn--sm">
                                                                    <input
                                                                        id={`upload-${folder.Id}`}
                                                                        type="file"
                                                                        multiple
                                                                        hidden
                                                                        onChange={(e) => {
                                                                            setSelectedFolderId(folder.Id);
                                                                            handleFileUpload(e);
                                                                        }}
                                                                    />

                                                                    <label
                                                                        htmlFor={`upload-${folder.Id}`}
                                                                        style={{ cursor: 'pointer', color: 'inherit' }}
                                                                    >
                                                                        Upload
                                                                    </label>

                                                                </button>

                                                                <button type="button" className="hv-btn hv-btn--ghost hv-btn--sm"
                                                                    onClick={() => {

                                                                        if (selectedRows.length == 0) {
                                                                            setShowSelectionPopup(true);

                                                                            setTimeout(() => {
                                                                                setShowSelectionPopup(false);
                                                                            }, 2000);

                                                                            return;
                                                                        }

                                                                        setMoveFolderId(folder.Id);
                                                                        setMoveFolderName(folder.Name);
                                                                        setShowMovePopup(true);

                                                                    }}
                                                                >

                                                                    Move

                                                                </button>

                                                                <span className="hv-pill hv-pill--info">

                                                                    {folder.Children.length} files

                                                                </span>

                                                            </div>

                                                        </div>


                                                        {expandedFolder === folder.Id && (


                                                            <div className="hv-tree__body">

                                                                {/* Toolbar */}
                                                                <div className="hv-tree__toolbar">

                                                                    <input
                                                                        className="hv-input hv-input--sm"
                                                                        style={{ maxWidth: 280 }}
                                                                        placeholder="Search files by name..."
                                                                        value={folderSearch[folder.Id] || ""}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value;

                                                                            setFolderSearch((prev) => ({
                                                                                ...prev,
                                                                                [folder.Id]: value,
                                                                            }));

                                                                            // Always go back to page 1 while searching
                                                                            setCurrentPage((prev) => ({
                                                                                ...prev,
                                                                                [folder.Id]: 1,
                                                                            }));
                                                                        }}
                                                                    />
                                                                    <div className="hv-row hv-row--tight">

                                                                        <button type="button" className="hv-btn hv-btn--sm">

                                                                            Fax Selected

                                                                        </button>

                                                                        <button type="button" className="hv-btn hv-btn--sm" onClick={handleMergePDF}>

                                                                            Merge to PDF

                                                                        </button>

                                                                    </div>
                                                                </div>
                                                                {showMergeValidation && (
                                                                    <>
                                                                        <div className="hv-scrim" />
                                                                        <div className="hv-modal hv-modal--top-right">
                                                                            <div className="hv-modal__box" style={{ width: 'min(350px, 100%)' }}>
                                                                                <div className="hv-modal__head">
                                                                                    <span className="hv-modal__title">Information</span>
                                                                                </div>

                                                                                <div className="hv-modal__body hv-t-center">
                                                                                    <p>Please Select at least two File.</p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {showMergeConfirm && (
                                                                    <>
                                                                        <div className="hv-scrim" onClick={() => setShowMergeConfirm(false)} />
                                                                        <div className="hv-modal hv-modal--top-right">
                                                                            <div className="hv-modal__box" style={{ width: 'min(500px, 100%)' }}>
                                                                                <div className="hv-modal__head">
                                                                                    <span className="hv-modal__title">Merge to PDF</span>
                                                                                </div>

                                                                                <div className="hv-modal__body">
                                                                                    <div className="hv-kv" style={{ marginBottom: 12 }}>
                                                                                        <span className="hv-kv__k">Patient</span>
                                                                                        <span className="hv-kv__v">{selectedPatientName}</span>
                                                                                    </div>

                                                                                    <p>Merge 2 file(s) from {folder.Name}</p>
                                                                                </div>

                                                                                <div className="hv-modal__foot">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="hv-btn hv-btn--ghost"
                                                                                        onClick={() => setShowMergeConfirm(false)}
                                                                                    >
                                                                                        Cancel
                                                                                    </button>

                                                                                    <button
                                                                                        type="button"
                                                                                        className="hv-btn"
                                                                                        onClick={handleconfirmMerge}
                                                                                    >
                                                                                        Confirm
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {showMergeSuccess && (
                                                                    <>
                                                                        <div className="hv-scrim" />
                                                                        <div className="hv-modal hv-modal--top-right">
                                                                            <div className="hv-modal__box" style={{ width: 'min(350px, 100%)' }}>
                                                                                <div className="hv-modal__head">
                                                                                    <span className="hv-modal__title">Success</span>
                                                                                </div>

                                                                                <div className="hv-modal__body hv-t-center">
                                                                                    <div className="hv-t-green" style={{ fontSize: 48, marginBottom: 12 }}>
                                                                                        ✓
                                                                                    </div>

                                                                                    <h3 className="hv-t-bold">
                                                                                        Merged file into
                                                                                        the All Merged Files Folder.
                                                                                    </h3>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {expandedFolder === folder.Id && childFolders.length > 0 && (
                                                                    <div className="hv-tree__children">

                                                                        {childFolders.map((subFolder) => (

                                                                            <div
                                                                                key={subFolder.Id}
                                                                                className="hv-tree__child"
                                                                            >
                                                                                <div className="hv-tree__label">

                                                                                    <button
                                                                                        type="button"
                                                                                        className="hv-tree__toggle"
                                                                                        onClick={() => handleExpand(folder.Id)}
                                                                                    >

                                                                                        {
                                                                                            expandedFolder == folder.Id ?

                                                                                                <ChevronDown size={18} />

                                                                                                :

                                                                                                <ChevronRight size={18} />

                                                                                        }

                                                                                    </button>

                                                                                    <Folder className="hv-t-amber" />

                                                                                    <div>

                                                                                        <div className="hv-tree__name">

                                                                                            {subFolder.Name}

                                                                                        </div>

                                                                                        <div className="hv-tree__meta">

                                                                                            {subFolder.FirstName} {subFolder.LastName}

                                                                                            &nbsp;

                                                                                            Created :

                                                                                            {subFolder.CreatedDate}

                                                                                        </div>

                                                                                    </div>

                                                                                </div>

                                                                                <div className="hv-tree__actions">

                                                                                    <Plus
                                                                                        size={17}
                                                                                        className="hv-t-teal"
                                                                                        style={{ cursor: 'pointer' }}
                                                                                        onClick={() => {

                                                                                            setParentFolderId(subFolder.Id);
                                                                                            setFolderName("");
                                                                                            setParentFolderName(subFolder.Name);
                                                                                            setShowSubFolderPopup(true);

                                                                                        }}
                                                                                    />

                                                                                    <Pencil size={15}
                                                                                        className="hv-t-teal"
                                                                                        style={{ cursor: 'pointer' }}
                                                                                        onClick={() => {

                                                                                            setParentFolderId(subFolder.Id);
                                                                                            setFolderName(subFolder.Name);
                                                                                            setShowRenameFolderPopup(true);

                                                                                        }}
                                                                                    />

                                                                                    <Trash2 size={15}
                                                                                        className="hv-t-red"
                                                                                        style={{ cursor: 'pointer' }}
                                                                                        onClick={() => {
                                                                                            setParentFolderId(subFolder.Id);
                                                                                            setShowDeletePopup(true);
                                                                                        }}
                                                                                    />

                                                                                    <button type="button" className="hv-btn hv-btn--sm">
                                                                                        <input
                                                                                            id={`upload-${subFolder.Id}`}
                                                                                            type="file"
                                                                                            multiple
                                                                                            hidden
                                                                                            onChange={(e) => {
                                                                                                setSelectedFolderId(subFolder.Id);
                                                                                                handleFileUpload(e);
                                                                                            }}
                                                                                        />

                                                                                        <label
                                                                                            htmlFor={`upload-${subFolder.Id}`}
                                                                                            style={{ cursor: 'pointer', color: 'inherit' }}
                                                                                        >
                                                                                            Upload
                                                                                        </label>

                                                                                    </button>

                                                                                    <button type="button" className="hv-btn hv-btn--ghost hv-btn--sm"
                                                                                        onClick={() => {

                                                                                            if (selectedRows.length == 0) {
                                                                                                setShowSelectionPopup(true);

                                                                                                setTimeout(() => {
                                                                                                    setShowSelectionPopup(false);
                                                                                                }, 2000);

                                                                                                return;
                                                                                            }

                                                                                            setMoveFolderId(subFolder.Id);
                                                                                            setMoveFolderName(subFolder.Name);
                                                                                            setShowMovePopup(true);

                                                                                        }}
                                                                                    >

                                                                                        Move

                                                                                    </button>

                                                                                    <span className="hv-pill hv-pill--info">

                                                                                        {subFolder.Children.length} files

                                                                                    </span>

                                                                                </div>

                                                                            </div>

                                                                        ))}

                                                                    </div>
                                                                )}

                                                                {/* Files */}
                                                                <div className="hv-table-scroll">
                                                                    <table className="hv-table">
                                                                        <tbody>
                                                                            {paginatedFiles.map((file, index) => {
                                                                                const serialNo =
                                                                                    current === "all"
                                                                                        ? index + 1
                                                                                        : (current - 1) * itemsPerPage + index + 1;

                                                                                return (
                                                                                    <tr key={file.Id}>
                                                                                        <td className="hv-t-center">
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={
                                                                                                    selectedFiles[expandedFolder]?.includes(file.DocumentID) || false
                                                                                                }
                                                                                                onChange={() => handleFileCheckbox(file)}
                                                                                            />
                                                                                        </td>

                                                                                        <td className="hv-t-center">
                                                                                            <span className="hv-pill hv-pill--info">
                                                                                                {serialNo}
                                                                                            </span>
                                                                                        </td>

                                                                                        <td>
                                                                                            <span className="hv-pill hv-pill--pending">
                                                                                                DOS : {file.DOS}
                                                                                            </span>
                                                                                        </td>

                                                                                        <td className="hv-td-strong">
                                                                                            {file.DocumentDescription}
                                                                                        </td>

                                                                                      
                                                                                        <td className="hv-t-center">

                                                                                            <FontAwesomeIcon className="hv-t-red" style={{ cursor: 'pointer' }} icon={faFilePdf}
                                                                                                onClick={() => {
                                                                                                    setPdfUrl(file.FileWebPath);
                                                                                                    setShowPdfPopup(true);
                                                                                                }}
                                                                                            />
                                                                                        </td>
                                                                                        <td>
                                                                                            <button type="button" className="hv-btn hv-btn--sm">

                                                                                                Fax

                                                                                            </button>
                                                                                        </td>

                                                                                        <td className="hv-t-center">
                                                                                            <Trash2
                                                                                                size={15}
                                                                                                className="hv-t-red"
                                                                                                style={{ cursor: 'pointer' }}
                                                                                                onClick={() => {
                                                                                                    setSelectedFile({
                                                                                                        documentId: file.DocumentID,
                                                                                                        folderId: folder.Id
                                                                                                    });

                                                                                                    setShowDeleteFilePopup(true);
                                                                                                }}
                                                                                            />
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                </div>

                                                                {/* Pagination */}
                                                                {files.length > itemsPerPage && (
                                                                    <div className="hv-tree__pagi">

                                                                        {/* Previous */}
                                                                        <button
                                                                            type="button"
                                                                            className="hv-pagi__btn"
                                                                            disabled={current === 1 || current === "all"}
                                                                            onClick={() =>
                                                                                setCurrentPage((prev) => ({
                                                                                    ...prev,
                                                                                    [folder.Id]: current - 1,
                                                                                }))
                                                                            }
                                                                        >
                                                                            &lt;
                                                                        </button>

                                                                        {/* Numbers */}
                                                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                                                            <button
                                                                                type="button"
                                                                                key={page}
                                                                                className={`hv-pagi__btn${current === page ? ' is-active' : ''}`}
                                                                                onClick={() =>
                                                                                    setCurrentPage((prev) => ({
                                                                                        ...prev,
                                                                                        [folder.Id]: page,
                                                                                    }))
                                                                                }
                                                                            >
                                                                                {page}
                                                                            </button>
                                                                        ))}

                                                                        {/* All */}
                                                                        <button
                                                                            type="button"
                                                                            className={`hv-pagi__btn${current === "all" ? ' is-active' : ''}`}
                                                                            onClick={() =>
                                                                                setCurrentPage((prev) => ({
                                                                                    ...prev,
                                                                                    [folder.Id]: "all",
                                                                                }))
                                                                            }
                                                                        >
                                                                            All
                                                                        </button>

                                                                        {/* Next */}
                                                                        <button
                                                                            type="button"
                                                                            className="hv-pagi__btn"
                                                                            disabled={current === totalPages || current === "all"}
                                                                            onClick={() =>
                                                                                setCurrentPage((prev) => ({
                                                                                    ...prev,
                                                                                    [folder.Id]: current + 1,
                                                                                }))
                                                                            }
                                                                        >
                                                                            &gt;
                                                                        </button>
                                                                    </div>
                                                                )}

                                                            </div>
                                                        )}
                                                    </div>


                                                </>
                                            )
                                        })}

                                </div>
                            </div>



                            {showFolderPopup && (
                                <>
                                    <div className="hv-scrim" onClick={() => setShowFolderPopup(false)} />
                                    <div className="hv-modal hv-modal--top-right">
                                        <div className="hv-modal__box" style={{ width: 'min(500px, 100%)' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">Create New Folder</span>
                                            </div>

                                            <div className="hv-modal__body">
                                                <div className="hv-kv" style={{ marginBottom: 12 }}>
                                                    <span className="hv-kv__k">Patient</span>
                                                    <span className="hv-kv__v">{selectedPatientName}</span>
                                                </div>

                                                <div className="hv-field">
                                                    <label className="hv-field__label">Folder name:</label>
                                                    <input
                                                        type="text"
                                                        value={folderName}
                                                        onChange={(e) => setFolderName(e.target.value)}
                                                        placeholder="New folder name"
                                                        className="hv-input"
                                                    />
                                                </div>
                                            </div>

                                            <div className="hv-modal__foot">
                                                <button type="button" className="hv-btn hv-btn--ghost" onClick={() => setShowFolderPopup(false)}>
                                                    Cancel
                                                </button>

                                                <button type="button" className="hv-btn" onClick={saveFolder}>
                                                    Save
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {showSubFolderPopup && (
                                <>
                                    <div className="hv-scrim" onClick={() => setShowSubFolderPopup(false)} />
                                    <div className="hv-modal hv-modal--top-right">
                                        <div className="hv-modal__box" style={{ width: 'min(500px, 100%)' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">Create New Folder in "{parentFolderName}"</span>
                                            </div>

                                            <div className="hv-modal__body">
                                                <div className="hv-kv" style={{ marginBottom: 12 }}>
                                                    <span className="hv-kv__k">Patient</span>
                                                    <span className="hv-kv__v">{selectedPatientName}</span>
                                                </div>

                                                <div className="hv-field">
                                                    <label className="hv-field__label">Folder name:</label>
                                                    <input
                                                        type="text"
                                                        value={folderName}
                                                        onChange={(e) => setFolderName(e.target.value)}
                                                        placeholder="New folder name"
                                                        className="hv-input"
                                                    />
                                                </div>
                                            </div>

                                            <div className="hv-modal__foot">
                                                <button type="button" className="hv-btn hv-btn--ghost" onClick={() => setShowSubFolderPopup(false)}>
                                                    Cancel
                                                </button>

                                                <button type="button" className="hv-btn" onClick={saveSubFolder}>
                                                    Save
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {showRenameFolderPopup && (
                                <>
                                    <div className="hv-scrim" onClick={() => setShowRenameFolderPopup(false)} />
                                    <div className="hv-modal hv-modal--top-right">
                                        <div className="hv-modal__box" style={{ width: 'min(500px, 100%)' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">Rename Folder</span>
                                            </div>

                                            <div className="hv-modal__body">
                                                <div className="hv-kv" style={{ marginBottom: 12 }}>
                                                    <span className="hv-kv__k">Patient</span>
                                                    <span className="hv-kv__v">{selectedPatientName}</span>
                                                </div>

                                                <div className="hv-field">
                                                    <label className="hv-field__label">Folder name:</label>
                                                    <input
                                                        type="text"
                                                        value={folderName}
                                                        onChange={(e) => setFolderName(e.target.value)}
                                                        placeholder="New folder name"
                                                        className="hv-input"
                                                    />
                                                </div>
                                            </div>

                                            <div className="hv-modal__foot">
                                                <button type="button" className="hv-btn hv-btn--ghost" onClick={() => setShowRenameFolderPopup(false)}>
                                                    Cancel
                                                </button>

                                                <button type="button" className="hv-btn" onClick={saveRenameFolder}>
                                                    Save
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}


                            {showDeletePopup && (
                                <>
                                    <div className="hv-scrim" onClick={() => { setShowDeletePopup(false); setParentFolderId(null); }} />
                                    <div className="hv-modal hv-modal--top-right">
                                        <div className="hv-modal__box" style={{ width: 'min(420px, 100%)' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">Delete Folder</span>
                                            </div>

                                            <div className="hv-modal__body hv-t-center">
                                                <div className="hv-t-red" style={{ fontSize: 40, marginBottom: 12 }}>
                                                    🗑️
                                                </div>

                                                <p>Are you sure you want to delete this folder?</p>
                                            </div>

                                            <div className="hv-modal__foot" style={{ justifyContent: 'center' }}>
                                                <button
                                                    type="button"
                                                    className="hv-btn hv-btn--ghost"
                                                    onClick={() => {
                                                        setShowDeletePopup(false);
                                                        setParentFolderId(null);
                                                    }}
                                                >
                                                    Cancel
                                                </button>

                                                <button
                                                    type="button"
                                                    className="hv-btn hv-btn--danger"
                                                    onClick={async () => {
                                                        await deleteFolder(parentFolderId);
                                                        setShowDeletePopup(false);
                                                        setParentFolderId(null);
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {successMessage && (
                                <>
                                    <div className="hv-scrim" />
                                    <div className="hv-modal hv-modal--top-right">
                                        <div className="hv-modal__box" style={{ width: 'min(350px, 100%)' }}>
                                            <div className="hv-modal__body hv-t-center">
                                                <div className="hv-t-green" style={{ fontSize: 48, marginBottom: 12 }}>
                                                    ✓
                                                </div>

                                                <h3 className="hv-t-bold">
                                                    {successMessage}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {showSelectionPopup && (
                                <>
                                    <div className="hv-scrim" />
                                    <div className="hv-modal hv-modal--top-right">
                                        <div className="hv-modal__box" style={{ width: 'min(350px, 100%)' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">Information</span>
                                            </div>

                                            <div className="hv-modal__body hv-t-center">
                                                <p>Please Select at least one File.</p>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {showMovePopup && (
                                <>
                                    <div className="hv-scrim" onClick={() => setShowMovePopup(false)} />
                                    <div className="hv-modal hv-modal--top-right">
                                        <div className="hv-modal__box" style={{ width: 'min(430px, 100%)' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">Move File</span>
                                            </div>

                                            <div className="hv-modal__body hv-t-center">
                                                <p>
                                                    Move selected
                                                    <b> {selectedRows.length} </b>
                                                    file(s) to folder
                                                    <b> "{moveFolderName}" </b>
                                                    ?
                                                </p>
                                            </div>

                                            <div className="hv-modal__foot" style={{ justifyContent: 'center' }}>
                                                <button type="button" className="hv-btn hv-btn--ghost" onClick={() => setShowMovePopup(false)}>
                                                    Cancel
                                                </button>

                                                <button type="button" className="hv-btn" onClick={handleMoveFiles}>
                                                    Move
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {showMoveSuccess && (
                                <>
                                    <div className="hv-scrim" />
                                    <div className="hv-modal hv-modal--top-right">
                                        <div className="hv-modal__box" style={{ width: 'min(350px, 100%)' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">Success</span>
                                            </div>

                                            <div className="hv-modal__body hv-t-center">
                                                <div className="hv-t-green" style={{ fontSize: 48, marginBottom: 12 }}>
                                                    ✓
                                                </div>

                                                <h3 className="hv-t-bold">
                                                    File Moved Successfully
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {showDeleteFilePopup && (
                                <>
                                    <div className="hv-scrim" onClick={() => { setShowDeleteFilePopup(false); setSelectedFile(null); }} />
                                    <div className="hv-modal hv-modal--top-right">
                                        <div className="hv-modal__box" style={{ width: 'min(430px, 100%)' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">Delete File</span>
                                            </div>

                                            <div className="hv-modal__body hv-t-center">
                                                <div className="hv-t-red" style={{ fontSize: 40, marginBottom: 12 }}>
                                                    🗑️
                                                </div>

                                                <p>Delete this file?</p>
                                                <p className="hv-t-faint hv-mt-2">
                                                    It will be permanently removed from the folder.
                                                </p>
                                            </div>

                                            <div className="hv-modal__foot" style={{ justifyContent: 'center' }}>
                                                <button
                                                    type="button"
                                                    className="hv-btn hv-btn--ghost"
                                                    onClick={() => {
                                                        setShowDeleteFilePopup(false);
                                                        setSelectedFile(null);
                                                    }}
                                                >
                                                    Cancel
                                                </button>

                                                <button type="button" className="hv-btn hv-btn--danger" onClick={deleteFile}>
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {showDeleteFileSuccess && (
                                <>
                                    <div className="hv-scrim" />
                                    <div className="hv-modal hv-modal--top-right">
                                        <div className="hv-modal__box" style={{ width: 'min(350px, 100%)' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">Success</span>
                                            </div>

                                            <div className="hv-modal__body hv-t-center">
                                                <div className="hv-t-green" style={{ fontSize: 48, marginBottom: 12 }}>
                                                    ✓
                                                </div>

                                                <div className="hv-t-bold">
                                                    File Deleted Successfully
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {showPdfPopup && (
                                <>
                                    <div className="hv-scrim" />
                                    <div className="hv-modal">
                                        <div className="hv-modal__box" style={{ width: '90vw', maxWidth: 'none', height: '90vh', maxHeight: '90vh' }}>
                                            <div className="hv-modal__head">
                                                <span className="hv-modal__title">PDF Preview</span>

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowPdfPopup(false);
                                                        setPdfUrl("");
                                                    }}
                                                    className="hv-modal__close"
                                                >
                                                    ×
                                                </button>
                                            </div>

                                            <div className="hv-modal__body" style={{ flex: '1 1 auto', padding: 0 }}>
                                                <iframe
                                                    src={pdfUrl}
                                                    title="PDF Viewer"
                                                    width="100%"
                                                    height="100%"
                                                    style={{ border: "none", display: 'block' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "sent" && (
                <div className="hv-stack">
                    <section className="hv-toolbar" aria-label="Sent fax filters">
                        <div className="hv-toolbar__group">
                            <label className="hv-field__label">From:</label>
                            <DatePicker
                                selected={startSentDate ? new Date(startSentDate) : null}
                                onChange={handleStartSentDateChange}
                                dateFormat="MM/dd/yyyy"
                                maxDate={new Date()}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                yearDropdownItemNumber={100}
                                className="hv-date"
                                placeholderText="MM/dd/yyyy"
                                calendarContainer={makeCalContainer("startSent")}
                                open={isStartOpen}
                                onFocus={() => setIsStartOpen(true)}
                                onInputClick={() => setIsStartOpen(true)}
                                onClickOutside={() => setIsStartOpen(false)}
                                renderCustomHeader={renderCalendarHeader}
                            />

                            <label className="hv-field__label">To:</label>
                            <DatePicker
                                selected={endSentDate ? new Date(endSentDate) : null}
                                onChange={handleEndSentDateChange}
                                dateFormat="MM/dd/yyyy"
                                maxDate={new Date()}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                yearDropdownItemNumber={100}
                                className="hv-date"
                                placeholderText="MM/dd/yyyy"
                                popperPlacement="bottom-start"
                                calendarContainer={makeCalContainer("endSent")}
                                open={isEndOpen}
                                onFocus={() => setIsEndOpen(true)}
                                onInputClick={() => setIsEndOpen(true)}
                                onClickOutside={() => setIsEndOpen(false)}
                                renderCustomHeader={renderCalendarHeader}
                            />

                            <button type="button" className="hv-btn" onClick={getSentFaxes}>
                                Search
                            </button>
                        </div>
                    </section>

                    <section className="hv-panel hv-panel--flush">
                        <div className="hv-table-scroll">
                            <table className="hv-table hv-table--striped">
                                <thead>
                                    <tr>
                                        <th className="hv-th-group--brand">#</th>
                                        <th className="hv-th-group--brand">Destination Number</th>
                                        <th className="hv-th-group--brand">Destination Name</th>
                                        <th className="hv-th-group--brand">Sent Date</th>
                                        <th className="hv-th-group--brand">Status</th>
                                        <th className="hv-th-group--brand">Resend</th>
                                        <th className="hv-th-group--brand">Delete</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading && (
                                        <tr>
                                            <td colSpan={7} className="hv-t-center">
                                                Loading...
                                            </td>
                                        </tr>
                                    )}

                                    {!loading &&
                                        Array.isArray(sentFaxes) &&
                                        sentFaxes.map((fax, index) => (
                                            <tr key={fax.ID || index}>
                                                <td className="hv-t-center">
                                                    {index + 1}
                                                </td>

                                                <td className="hv-td-nowrap">
                                                    {fax.DestinationNumber}
                                                </td>

                                                <td>
                                                    {fax.DestinationName}
                                                </td>

                                                <td className="hv-td-nowrap">
                                                    <div className="hv-row hv-row--tight">
                                                        {fax.sentdate}
                                                        <span>{fax.sentTime}</span>

                                                        <FontAwesomeIcon
                                                            icon={faFilePdf}
                                                            className="hv-t-red"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setPdfUrl(fax.PDFURL);
                                                                setShowPdfPopup(true);
                                                            }}
                                                        />
                                                    </div>
                                                </td>

                                                <td className="hv-t-center">
                                                    <span
                                                        className={
                                                            fax.Status?.toLowerCase() === "success"
                                                                ? "hv-t-green"
                                                                : "hv-t-red"
                                                        }
                                                    >
                                                        {fax.Status}
                                                    </span>
                                                </td>

                                                <td className="hv-t-center">
                                                    <RotateCcw
                                                        size={18}
                                                        className="hv-t-teal"
                                                        style={{ cursor: 'pointer', margin: '0 auto' }}
                                                    />
                                                </td>

                                                <td className="hv-t-center">
                                                    <input className="hv-th-check" type="checkbox" />
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            )}
            </div>
        </div>
    );
}
